import { Dice5, Target, BarChart3 } from "lucide-react";

const C = {
  surface: "var(--bc-panel, #18181B)", highlight: "var(--bc-panel-elev, #27272A)", border: "var(--bc-border, #27272A)",
  text: "var(--bc-ink, #FAFAFA)", muted: "var(--bc-sub, #A1A1AA)", green: "#22C55E",
  red: "#EF4444", blue: "#3B82F6", yellow: "#F59E0B", cyan: "#06B6D4",
  purple: "#A78BFA",
};

function MarketRow({ label, values, colors }) {
  const max = Math.max(...values.map((v) => v.pct));
  return (
    <div
      data-testid={`mc-row-${label}`}
      className="grid items-center gap-3 py-2.5 px-3"
      style={{
        gridTemplateColumns: "110px 1fr",
        borderBottom: `1px solid ${C.highlight}`,
      }}
    >
      <span
        className="font-heading font-bold text-[11px] uppercase tracking-wider"
        style={{ color: C.muted }}
      >
        {label}
      </span>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${values.length}, 1fr)` }}
      >
        {values.map((v, i) => {
          const color = colors[i];
          const isMax = v.pct === max;
          return (
            <div key={i} className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <span
                  className="font-heading font-bold text-[10px] uppercase truncate"
                  style={{ color: C.muted }}
                >
                  {v.name}
                </span>
                <span
                  className="font-data font-bold text-xs tabular-nums"
                  style={{ color: isMax ? color : C.muted }}
                >
                  {v.pct}%
                </span>
              </div>
              <div
                className="h-1.5 relative overflow-hidden"
                style={{ background: C.highlight }}
              >
                <div
                  className="absolute top-0 left-0 h-full transition-all"
                  style={{
                    width: `${v.pct}%`,
                    background: color,
                    boxShadow: isMax ? `0 0 8px ${color}80` : "none",
                  }}
                />
                {isMax && (
                  <div
                    className="absolute top-0 h-full w-[2px]"
                    style={{
                      left: `${v.pct}%`,
                      background: "rgba(255,255,255,0.9)",
                      boxShadow: "0 0 6px rgba(255,255,255,0.8)",
                      transform: "translateX(-1px)",
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GoalsDistribution({ ci }) {
  if (!ci) return null;
  const { mean, std, p10, p50, p90, min, max } = ci;
  // Scale the bar to a tight, realistic window around the p80 interval
  // rather than the extreme min/max (which includes rare outliers).
  const axisMax = Math.max(p90 + 1, Math.ceil(mean) + 1, 5);
  const axisMin = 0;
  const scale = (v) => ((v - axisMin) / (axisMax - axisMin)) * 100;
  return (
    <div
      className="p-4"
      style={{
        background: "rgba(6,182,212,0.04)",
        border: `1px solid ${C.highlight}`,
      }}
      data-testid="goals-distribution"
    >
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={12} style={{ color: C.cyan }} />
        <span
          className="font-heading font-bold text-xs uppercase tracking-widest"
          style={{ color: C.cyan }}
        >
          DISTRIBUZIONE GOL
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <div className="font-data text-[10px] uppercase tracking-wider" style={{ color: C.muted }}>
            Media
          </div>
          <div className="font-data font-bold text-lg" style={{ color: C.cyan }}>
            {mean}
            <span className="text-xs ml-1" style={{ color: C.muted }}>±{std}</span>
          </div>
        </div>
        <div>
          <div className="font-data text-[10px] uppercase tracking-wider" style={{ color: C.muted }}>
            Mediana
          </div>
          <div className="font-data font-bold text-lg" style={{ color: C.text }}>
            {p50}
          </div>
        </div>
        <div>
          <div className="font-data text-[10px] uppercase tracking-wider" style={{ color: C.muted }}>
            Range 80%
          </div>
          <div className="font-data font-bold text-lg" style={{ color: C.text }}>
            {p10}–{p90}
          </div>
        </div>
      </div>
      <div className="h-2 relative overflow-hidden" style={{ background: C.highlight }}>
        <div
          className="absolute top-0 h-full"
          style={{
            left: `${scale(p10)}%`,
            width: `${scale(p90) - scale(p10)}%`,
            background: `linear-gradient(90deg, ${C.cyan}30, ${C.cyan}70, ${C.cyan}30)`,
            boxShadow: `0 0 10px ${C.cyan}50`,
          }}
        />
        <div
          className="absolute top-0 h-full w-[2px]"
          style={{
            left: `${scale(mean)}%`,
            background: "rgba(255,255,255,0.95)",
            boxShadow: "0 0 6px rgba(255,255,255,0.8)",
          }}
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className="font-data text-[10px]" style={{ color: C.muted }}>{axisMin} gol</span>
        <span className="font-data text-[10px]" style={{ color: C.muted }}>{axisMax}+ gol</span>
      </div>
    </div>
  );
}

function MarginBar({ ci, homeName, awayName }) {
  if (!ci) return null;
  const { mean, p10, p50, p90 } = ci;
  const favored = mean > 0 ? homeName : mean < 0 ? awayName : "equilibrato";
  const color = mean > 0.5 ? C.green : mean < -0.5 ? C.red : C.yellow;
  return (
    <div
      className="p-4"
      style={{
        background: `${color}06`,
        border: `1px solid ${color}25`,
      }}
      data-testid="margin-distribution"
    >
      <div className="flex items-center gap-2 mb-3">
        <Target size={12} style={{ color }} />
        <span className="font-heading font-bold text-xs uppercase tracking-widest" style={{ color }}>
          MARGINE ATTESO
        </span>
        <span className="font-data text-[10px] ml-auto" style={{ color: C.muted }}>
          (casa − ospite)
        </span>
      </div>
      <div className="flex items-baseline gap-3 mb-2">
        <span
          className="font-data font-black text-3xl"
          style={{ color, textShadow: `0 0 12px ${color}60` }}
        >
          {mean > 0 ? "+" : ""}{mean}
        </span>
        <span className="font-heading font-bold text-xs uppercase tracking-wider" style={{ color: C.muted }}>
          favorita: <span style={{ color }}>{favored}</span>
        </span>
      </div>
      <div className="font-data text-[11px]" style={{ color: C.muted }}>
        Intervallo 80%: {p10 > 0 ? "+" : ""}{p10} / {p90 > 0 ? "+" : ""}{p90} gol · Mediana {p50 > 0 ? "+" : ""}{p50}
      </div>
    </div>
  );
}

export default function MonteCarloPanel({ monteCarlo, homeTeam, awayTeam }) {
  if (!monteCarlo || !monteCarlo.markets_1x2) return null;

  const { markets_1x2, markets_ou25, markets_btts } = monteCarlo;
  const hn = (homeTeam || "Casa").slice(0, 14);
  const an = (awayTeam || "Ospite").slice(0, 14);

  return (
    <div
      data-testid="monte-carlo-panel"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderTop: `2px solid ${C.purple}`,
      }}
    >
      {/* HEADER */}
      <div
        className="px-4 pt-4 pb-3 flex items-center gap-2"
        style={{ borderBottom: `1px solid ${C.highlight}` }}
      >
        <Dice5 size={14} style={{ color: C.purple }} />
        <span
          className="font-heading font-black text-xs uppercase tracking-[0.2em]"
          style={{ color: C.purple }}
        >
          MONTE CARLO
        </span>
        <span
          className="font-data text-[10px] uppercase tracking-widest"
          style={{ color: C.muted }}
        >
          · Mercati Principali
        </span>
        <span
          className="font-data text-[10px] uppercase tracking-wider ml-auto px-2 py-0.5"
          style={{
            color: C.purple,
            background: "rgba(167,139,250,0.08)",
            border: "1px solid rgba(167,139,250,0.2)",
          }}
        >
          {(monteCarlo.n_sims || 10000).toLocaleString()} SIM
        </span>
      </div>

      {/* MARKETS */}
      <div className="p-4">
        <div style={{ border: `1px solid ${C.highlight}` }}>
          <MarketRow
            label="Esito 1X2"
            values={[
              { name: hn, pct: markets_1x2.home },
              { name: "Pareggio", pct: markets_1x2.draw },
              { name: an, pct: markets_1x2.away },
            ]}
            colors={[C.green, C.yellow, C.red]}
          />
          <MarketRow
            label="Over/Under 2.5"
            values={[
              { name: "Over 2.5", pct: markets_ou25.over },
              { name: "Under 2.5", pct: markets_ou25.under },
            ]}
            colors={[C.cyan, C.blue]}
          />
          <MarketRow
            label="Gol / No Gol"
            values={[
              { name: "Gol/Gol", pct: markets_btts.yes },
              { name: "No Gol/Gol", pct: markets_btts.no },
            ]}
            colors={[C.green, C.red]}
          />
        </div>

        {/* GOALS CI & MARGIN */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">
          <GoalsDistribution ci={monteCarlo.goals_ci} />
          <MarginBar ci={monteCarlo.margin_ci} homeName={hn} awayName={an} />
        </div>

        {/* Footer info */}
        <div
          className="mt-3 pt-3 flex items-start gap-2"
          style={{ borderTop: `1px solid ${C.highlight}` }}
        >
          <div
            className="font-data text-[10px] leading-relaxed"
            style={{ color: C.muted }}
          >
            <strong style={{ color: C.purple }}>Come leggere</strong>: 10.000
            simulazioni Dixon-Coles per partita stimano la frequenza con cui
            ciascun esito si realizza. La linea bianca indica l'esito dominante.
            Il range 80% sul totale gol è l'intervallo di confidenza sul modello.
          </div>
        </div>
      </div>
    </div>
  );
}
